/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {FC, useEffect} from 'react';
import Modal from 'components/Modal/Modal';
import {Box, Button, Grid, ModalProps, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import './LeaveRequestFilterModal.scss';
import SearchBarController from 'controllers/SearchBarController/SearchBarController';
import {useForm} from 'react-hook-form';
import DropDownInput from 'components/DropDownInput';
import Chip from 'components/Chip/Chip';
import {
  LeaveRequestFilterData,
  leaveStatusArray,
  leaveStatusLabels,
} from 'models/leaveRequest';
import DateRangePicker from 'components/DateRangePicker/DateRangePicker';
import GqlQueryController from 'controllers/GqlQueryController/GqlQueryController';
import {FILTER_USERS, GET_CATALOGS_BY_TYPE} from 'graphql/absences/Querys';
import {Catalog} from 'models/catalog';

interface LeaveRequestFilterModalProps
  extends Omit<ModalProps, 'children' | 'onSubmit' | 'open'> {
  onSubmit: (data: LeaveRequestFilterData | undefined) => void;
  inline?: boolean;
  open?: boolean;
}

interface UserAutocompleteItem {
  id: string;
  firstName: string;
  lastName: string;
}

const LeaveRequestFilterModal: FC<LeaveRequestFilterModalProps> = ({
  onClose,
  onSubmit: submitCallback,
  inline = false,
  open = true,
  ...other
}) => {
  const {t} = useTranslation();
  const [selectedNameLabel, setSelectedNameLabel] = React.useState('');

  const {register, handleSubmit, getValues, setValue} =
    useForm<LeaveRequestFilterData>({
      defaultValues: {
        name: '',
        status: '',
        leaveRequest: {startDate: null, endDate: null},
        leavePeriod: {startDate: null, endDate: null},
        countryCode: '',
      },
    });

  const getStatusOptions = () => [
    {
      key: '',
      description: t('leaves.LeaveRequestFilterModal.selectPlaceholder'),
    },
    ...leaveStatusArray.map(status => {
      return {key: status, description: t(leaveStatusLabels(String(status)))};
    }),
  ];

  const getCountryCodeOptions = (countryCodes: Catalog[]) => {
    const options = [
      {
        key: '',
        description: t('leaves.LeaveRequestFilterModal.selectPlaceholder'),
      },
    ];
    if (countryCodes && countryCodes.length > 0) {
      countryCodes.forEach((catalog: Catalog) => {
        // Si el catálogo tiene children, usar los children
        if (catalog.children && catalog.children.length > 0) {
          catalog.children.forEach((child: Catalog) => {
            options.push({key: child.name, description: child.description});
          });
        } else {
          // Si no tiene children, usar el catálogo directamente
          options.push({key: catalog.name, description: catalog.description});
        }
      });
    }
    return options;
  };

  const onSubmit = (data: any) => {
    const normalizedData: LeaveRequestFilterData = {};

    if (data.name?.trim()) {
      normalizedData.name = data.name.trim();
    }

    if (data.status) {
      normalizedData.status = data.status;
    }

    if (data.countryCode) {
      normalizedData.countryCode = data.countryCode;
    }

    if (data.leaveRequest?.startDate && data.leaveRequest?.endDate) {
      normalizedData.leaveRequest = data.leaveRequest;
    }

    if (data.leavePeriod?.startDate && data.leavePeriod?.endDate) {
      normalizedData.leavePeriod = data.leavePeriod;
    }

    submitCallback(
      Object.keys(normalizedData).length > 0 ? normalizedData : undefined
    );
    if (!inline && onClose) {
      onClose({}, 'backdropClick');
    }
  };

  const renderNameInput = () => {
    return (
      <>
        <SearchBarController<UserAutocompleteItem>
          query={FILTER_USERS}
          queryVariableName="keyword"
          queryDataPath={['filterUsers']}
          placeholder={t('AbsenceSummary.search')}
          transformResult={user => ({
            id: user.id,
            text: `${user.firstName} ${user.lastName}`,
            selected: false,
          })}
          onResultSelect={user => {
            const fullName = `${user.firstName} ${user.lastName}`;
            setValue('name', fullName, {
              shouldValidate: true,
            });
            setSelectedNameLabel(fullName);
          }}
        />
        {selectedNameLabel && (
          <Chip
            id="selected-user"
            text={selectedNameLabel}
            size="small"
            color="primary"
          />
        )}
      </>
    );
  };

  const StatusDropDown = React.memo(() => {
    return (
      <DropDownInput
        {...register('status')}
        children={getStatusOptions()}
        defaultValue={getValues('status')}
        noSelection=""
      />
    );
  });

  const CountryCodeDropDown = React.memo(
    ({countryCodes}: {countryCodes: Catalog[]}) => {
      return (
        <DropDownInput
          {...register('countryCode')}
          children={getCountryCodeOptions(countryCodes)}
          defaultValue={getValues('countryCode')}
          noSelection=""
        />
      );
    }
  );

  const DateRangeInput = React.memo(
    ({field}: {field: 'leaveRequest' | 'leavePeriod'}) => {
      useEffect(() => {
        register(field);
      }, [field]);

      const lastValue = getValues(field);
      return (
        <DateRangePicker
          label={''}
          defaultValue={lastValue}
          onChangeDates={(startDate: Date | null, endDate: Date | null) => {
            if (startDate && endDate) {
              setValue(field, {startDate, endDate});
            }
          }}
        />
      );
    }
  );

  const FilterFormItem = ({
    label,
    children,
  }: {
    label: string;
    children?: React.ReactNode;
  }) => {
    return (
      <Grid item xs={12} display="flex" flexDirection="column">
        <Typography variant="subtitle1">{label}</Typography>
        {children}
      </Grid>
    );
  };

  const formContent = (
    <GqlQueryController
      QUERY={GET_CATALOGS_BY_TYPE}
      variables={{type: 'COUNTRY_CODE'}}
      initialState={[] as Catalog[]}
      rootField="catalogsByType"
    >
      {({data: countryCodes}) => (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="flex"
            flexDirection="column"
            sx={{minHeight: inline ? 0 : 520}}
          >
            <Grid container className="filter-form-container" spacing={2}>
              <FilterFormItem label={t('leaves.LeaveRequestFilterModal.name')}>
                {renderNameInput()}
              </FilterFormItem>

              <FilterFormItem
                label={t('leaves.LeaveRequestFilterModal.status')}
              >
                <StatusDropDown />
              </FilterFormItem>

              <FilterFormItem
                label={t('leaves.LeaveRequestFilterModal.country')}
              >
                <CountryCodeDropDown countryCodes={countryCodes} />
              </FilterFormItem>

              <FilterFormItem
                label={t('leaves.LeaveRequestFilterModal.requestDate')}
              >
                <DateRangeInput field="leaveRequest" />
              </FilterFormItem>

              <FilterFormItem
                label={t('leaves.LeaveRequestFilterModal.leavePeriod')}
              >
                <DateRangeInput field="leavePeriod" />
              </FilterFormItem>
            </Grid>

            <Grid
              item
              xs={12}
              className={'mt-2'}
              display="flex"
              justifyContent="flex-end"
              alignItems="end"
              sx={{
                flexGrow: 1, // Allows this grid to take up remaining space
              }}
            >
              {!inline && (
                <Button
                  variant="outlined"
                  onClick={event => onClose && onClose(event, 'backdropClick')}
                >
                  {t('leaves.LeaveRequestFilterModal.exit')}
                </Button>
              )}
              <Button variant="contained" type="submit">
                {t('leaves.LeaveRequestFilterModal.submit')}
              </Button>
            </Grid>
          </Box>
        </form>
      )}
    </GqlQueryController>
  );

  if (inline) {
    return (
      <Box className="mt-4 mb-2 p-4 rounded border border-gray-200 bg-white">
        <Typography variant="h6">
          {t('leaves.LeaveRequestFilterModal.title')}
        </Typography>
        <Typography variant="body2" sx={{mb: 2}}>
          {t('leaves.LeaveRequestFilterModal.description')}
        </Typography>
        {formContent}
      </Box>
    );
  }

  return (
    <Modal
      open={open}
      title={t('leaves.LeaveRequestFilterModal.title')}
      description={t('leaves.LeaveRequestFilterModal.description')}
      onClose={onClose}
      {...other}
    >
      {formContent}
    </Modal>
  );
};

export default LeaveRequestFilterModal;
